import { useCallback, useMemo } from "react";
import {
  Stack,
  Button,
  Group,
  Select,
  Switch,
  SegmentedControl,
  Divider,
  Modal,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useQuery } from "@tanstack/react-query";
import { insuranceProvidersService } from "../../../services/admin/insuranceProviders.service";

export interface EligibilityRuleDraft {
  key: string;
  insuranceProviderId: string | null;
  insuranceProviderName: string | null;
  isCatchAll: boolean;
  tcpcEligible: boolean;
}

type EligibilityRuleType = "specific" | "catch_all";

interface AddEligibilityRuleFormValues {
  ruleType: EligibilityRuleType;
  providerId: string;
  tcpcEligible: boolean;
}

interface AddEligibilityRuleFormProps {
  existingProviderIds: string[];
  hasCatchAll: boolean;
  onConfirm: (draft: EligibilityRuleDraft) => void;
  onClose: () => void;
}

export default function AddEligibilityRuleForm({
  existingProviderIds,
  hasCatchAll,
  onConfirm,
  onClose,
}: AddEligibilityRuleFormProps) {
  const form = useForm<AddEligibilityRuleFormValues>({
    initialValues: {
      ruleType: "specific",
      providerId: "",
      tcpcEligible: false,
    },
    validate: {
      providerId: (value, values) =>
        values.ruleType === "specific" && !value ? "Choose a carrier" : null,
    },
  });

  const { data: insuranceProviders = [] } = useQuery({
    queryKey: ["insuranceProviders"],
    queryFn: () => insuranceProvidersService.getAll(),
  });

  const providerOptions = useMemo(
    () =>
      insuranceProviders
        .filter((p) => !existingProviderIds.includes(p.id))
        .map((p) => ({ value: p.id, label: p.name })),
    [insuranceProviders, existingProviderIds]
  );

  const typeOptions = useMemo(
    () => [
      { label: "Specific carrier", value: "specific" },
      {
        label: "All other carriers",
        value: "catch_all",
        disabled: hasCatchAll,
      },
    ],
    [hasCatchAll]
  );

  const handleTypeChange = useCallback(
    (value: string) => {
      form.setFieldValue("ruleType", value as EligibilityRuleType);
      if (value === "catch_all") {
        // A catch-all rule has no specific carrier.
        form.setFieldValue("providerId", "");
      }
    },
    [form]
  );

  const handleProviderChange = useCallback(
    (value: string | null) => {
      form.setFieldValue("providerId", value ?? "");
    },
    [form]
  );

  const handleSubmit = useCallback(
    (values: AddEligibilityRuleFormValues) => {
      const isCatchAll = values.ruleType === "catch_all";
      const provider = insuranceProviders.find(
        (p) => p.id === values.providerId
      );
      onConfirm({
        key: crypto.randomUUID(),
        insuranceProviderId: isCatchAll ? null : values.providerId,
        insuranceProviderName: isCatchAll ? null : provider?.name ?? null,
        isCatchAll,
        tcpcEligible: values.tcpcEligible,
      });
      onClose();
    },
    [insuranceProviders, onConfirm, onClose]
  );

  const canSubmit = useMemo(
    () =>
      form.values.ruleType === "catch_all" ||
      (form.values.ruleType === "specific" && Boolean(form.values.providerId)),
    [form.values.ruleType, form.values.providerId]
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Modal.Body style={{ flex: 1, overflow: "auto" }}>
        <Stack gap="md">
          <SegmentedControl
            fullWidth
            data={typeOptions}
            value={form.values.ruleType}
            onChange={handleTypeChange}
          />

          {form.values.ruleType === "specific" && (
            <Select
              label="Insurance carrier"
              placeholder="Select insurance carrier"
              data={providerOptions}
              value={form.values.providerId || null}
              onChange={handleProviderChange}
              searchable
              clearable
              error={form.errors.providerId}
            />
          )}

          <Switch
            label={
              form.values.tcpcEligible ? "TCPC Eligible" : "Not TCPC Eligible"
            }
            color={form.values.tcpcEligible ? "green" : "red"}
            {...form.getInputProps("tcpcEligible", { type: "checkbox" })}
          />
        </Stack>
      </Modal.Body>

      <Divider />
      <Group justify="space-between" p="md">
        <Button variant="default" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSubmit}>
          Add Rule
        </Button>
      </Group>
    </form>
  );
}
